import { Injectable, Signal, WritableSignal, inject, signal } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';

import { AuthorService } from 'src/app/services/self-serve/author.service';
import { IPersonaInfo } from 'src/app/interfaces/self-serve/assignment-manager/ipersona-info';

export interface SelfAuthoringPersonaSnapshot {
  uuid?: string;
  personaId?: string;
  personaName?: string;
  replicaId?: string;
  gender?: string;
  welcomeMessage?: string[];
  resumeMessage?: string[];
}

interface GeneratedPersonaDetails {
  avatarName: string | null;
  avatarGender?: string | null;
  welcomeMessage: string[];
  resumeMessage: string[];
}

type MessageArrayControlName = 'welcomeMessage' | 'resumeMessage';

/** 'custom' for roleplay personas, 'echo' for Knowledge Check (GUIDED) personas. */
export type PersonaMode = 'custom' | 'echo';

/**
 * Single source of truth for self-authoring's persona create/update flow.
 * Independent of AuthorReactiveFormService/the full simulation form — the
 * dashboard's generation-review modal shows avatar selection before any
 * simulation form is ever registered there, so this service owns its own
 * lightweight FormGroup per callUuid and reconciles with the backend
 * (createPersona/updatePersona) directly.
 */
@Injectable({ providedIn: 'root' })
export class SelfAuthoringPersonaFormService {
  private readonly authorService = inject(AuthorService);
  private readonly fb = inject(FormBuilder);

  private readonly forms = new Map<string, FormGroup>();
  private readonly inFlight = new Map<string, boolean>();
  private readonly pending = new Map<string, boolean>();
  private readonly savingSignals = new Map<string, WritableSignal<boolean>>();
  private readonly modes = new Map<string, PersonaMode>();

  isSaving(callUuid: string): Signal<boolean> {
    return this.savingSignalFor(callUuid).asReadonly();
  }

  private savingSignalFor(callUuid: string): WritableSignal<boolean> {
    let savingSignal = this.savingSignals.get(callUuid);
    if (!savingSignal) {
      savingSignal = signal(false);
      this.savingSignals.set(callUuid, savingSignal);
    }
    return savingSignal;
  }

  getOrCreateForm(callUuid: string): FormGroup {
    let form = this.forms.get(callUuid);
    if (!form) {
      form = this.fb.group({
        uuid: [''],
        personaId: [''],
        personaName: [''],
        replicaId: [''],
        gender: [''],
        welcomeMessage: this.fb.array([this.fb.control('')]),
        resumeMessage: this.fb.array([this.fb.control('')]),
      });
      this.forms.set(callUuid, form);
    }
    return form;
  }

  /** Gets/creates the form for this call and fills in already-known persona data, without triggering a save. */
  initializeForm(
    callUuid: string,
    data: SelfAuthoringPersonaSnapshot,
    options: { mode?: PersonaMode } = {}
  ): FormGroup {
    this.modes.set(callUuid, options.mode ?? 'custom');
    const form = this.getOrCreateForm(callUuid);
    const { welcomeMessage, resumeMessage, ...scalarFields } = data;
    form.patchValue(scalarFields, { emitEvent: false });
    if (welcomeMessage?.length) {
      this.setMessageArray(form, 'welcomeMessage', welcomeMessage);
    }
    if (resumeMessage?.length) {
      this.setMessageArray(form, 'resumeMessage', resumeMessage);
    }
    return form;
  }

  /** Writes avatar name/welcome/resume messages from the generated call-details response, and saves to the server if that changed anything. */
  saveGeneratedDetails(callUuid: string, details: GeneratedPersonaDetails): void {
    const form = this.getOrCreateForm(callUuid);
    let changed = false;

    if (details.avatarName && form.get('personaName')?.value !== details.avatarName) {
      form.get('personaName')?.setValue(details.avatarName, { emitEvent: false });
      changed = true;
    }

    // Roleplay gender comes from the simulation overview. It isn't counted as a change on its
    // own; it goes out with this or the next save.
    if (details.avatarGender && !this.isEcho(callUuid)) {
      form.get('gender')?.setValue(details.avatarGender, { emitEvent: false });
    }

    if (this.applyMessageArrayIfChanged(form, 'welcomeMessage', details.welcomeMessage)) {
      changed = true;
    }

    if (this.applyMessageArrayIfChanged(form, 'resumeMessage', details.resumeMessage)) {
      changed = true;
    }

    if (changed) {
      this.evaluateAndSave(callUuid, form);
    }
  }

  updateAvatarSelection(callUuid: string, replicaId: string | undefined, gender?: string): void {
    if (!replicaId) {
      return;
    }
    const form = this.getOrCreateForm(callUuid);
    // Knowledge Check gender comes from the replica; roleplay gender comes from the overview (saveGeneratedDetails).
    if (this.isEcho(callUuid)) {
      form.get('gender')?.setValue(gender ?? '');
    }
    form.get('replicaId')?.setValue(replicaId);
    this.evaluateAndSave(callUuid, form);
  }

  /** Stores the saved Knowledge Check avatar's gender once replicas load, so later saves send it. Doesn't save. */
  setReplicaGender(callUuid: string, gender: string | undefined): void {
    if (!gender || !this.isEcho(callUuid)) {
      return;
    }
    this.getOrCreateForm(callUuid).get('gender')?.setValue(gender, { emitEvent: false });
  }

  private applyMessageArrayIfChanged(
    form: FormGroup,
    controlName: MessageArrayControlName,
    messages: string[]
  ): boolean {
    if (!messages?.length) {
      return false;
    }
    const current = (form.get(controlName) as FormArray).value as string[];
    const isSame =
      current.length === messages.length && current.every((value, index) => value === messages[index]);
    if (isSame) {
      return false;
    }
    this.setMessageArray(form, controlName, messages);
    return true;
  }

  private setMessageArray(form: FormGroup, controlName: MessageArrayControlName, messages: string[]): void {
    const array = form.get(controlName) as FormArray;
    array.clear({ emitEvent: false });
    messages.forEach((message) => array.push(this.fb.control(message), { emitEvent: false }));
  }

  /** Saves the persona if it is complete, e.g. after the avatar name was edited. */
  saveIfComplete(callUuid: string): void {
    this.evaluateAndSave(callUuid, this.getOrCreateForm(callUuid));
  }

  private evaluateAndSave(callUuid: string, form: FormGroup): void {
    if (!this.isComplete(callUuid, form)) {
      return;
    }
    this.save(callUuid, form);
  }

  private isComplete(callUuid: string, form: FormGroup): boolean {
    const personaName = (form.get('personaName')?.value ?? '').trim();
    const replicaId = form.get('replicaId')?.value;
    // Echo (Knowledge Check) personas have no greeting messages.
    if (this.isEcho(callUuid)) {
      return !!personaName && !!replicaId;
    }
    const welcomeMessages = (form.get('welcomeMessage') as FormArray).value as string[];
    return !!personaName && !!replicaId && welcomeMessages.some((message) => !!message?.trim());
  }

  private save(callUuid: string, form: FormGroup): void {
    if (this.inFlight.get(callUuid)) {
      this.pending.set(callUuid, true);
      return;
    }

    this.inFlight.set(callUuid, true);
    this.pending.set(callUuid, false);
    this.savingSignalFor(callUuid).set(true);

    const personaId = form.get('personaId')?.value;
    const request$ = personaId
      ? this.authorService.updatePersonaInfo(this.buildUpdatePayload(form, callUuid, personaId))
      : this.authorService.createPersonaInfo(this.buildCreatePayload(form, callUuid));

    request$.subscribe({
      next: (response) => {
        form.patchValue({
          uuid: response.uuid ?? '',
          personaId: response.personaId,
          replicaId: response.replicaId,
        });
        this.inFlight.set(callUuid, false);
        this.savingSignalFor(callUuid).set(false);
        this.resaveIfPending(callUuid, form);
      },
      error: () => {
        this.inFlight.set(callUuid, false);
        this.savingSignalFor(callUuid).set(false);
        this.resaveIfPending(callUuid, form);
      },
    });
  }

  private resaveIfPending(callUuid: string, form: FormGroup): void {
    if (this.pending.get(callUuid)) {
      this.pending.set(callUuid, false);
      this.save(callUuid, form);
    }
  }

  private buildCreatePayload(form: FormGroup, callUuid: string) {
    return {
      mode: this.modes.get(callUuid) ?? 'custom',
      callUuid,
      personaName: form.get('personaName')?.value,
      replicaId: form.get('replicaId')?.value,
      ...this.genderField(form),
      welcomeMessage: (form.get('welcomeMessage') as FormArray).value,
      resumeMessage: (form.get('resumeMessage') as FormArray).value,
      // Echo personas have no custom LLM layer, so the transcription flow (LLM base_url) doesn't apply.
      ...(this.isEcho(callUuid) ? {} : { useWithNewTranscription: true }),
    };
  }

  private buildUpdatePayload(form: FormGroup, callUuid: string, personaId: string): IPersonaInfo {
    return {
      personaId,
      callUuid,
      personaName: form.get('personaName')?.value,
      replicaId: form.get('replicaId')?.value,
      ...this.genderField(form),
      welcomeMessage: (form.get('welcomeMessage') as FormArray).value,
      resumeMessage: (form.get('resumeMessage') as FormArray).value,
      // Sending this patches /layers/llm/base_url in Tavus, which fails for echo personas.
      ...(this.isEcho(callUuid) ? {} : { useWithNewTranscription: true }),
    };
  }

  /** Omitted when unknown so the backend keeps what it has. */
  private genderField(form: FormGroup): { gender?: string } {
    const gender = form.get('gender')?.value;
    return gender ? { gender } : {};
  }

  private isEcho(callUuid: string): boolean {
    return this.modes.get(callUuid) === 'echo';
  }
}
